import PropTypes from 'prop-types';

import {
  trans,
  getPagePath,
  useSoyuzLocales,
  useSoyuzRequest
} from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useIsUserLogged
} from '@spotahome/ui-library';
import { getDateAsUTC } from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';
import { HomecardFormatter } from '@spotahome/ui-library/src/tenant/utils/images';
import { useEcommerceManagerFactory } from '@spotahome/ecommerce-tracking';

import { getMonthlyPrice } from '../../utils/homecards';

import { getCurrencySymbol } from '../../utils/CurrencyFormatter';

import HomeCardPlaceHolder from '../Homecard/HomeCardPlaceHolder';
import Homecard from '../Homecard';

import { useAlternatives } from './utils/useAlternatives';

import './AlternativesModal.scss';

const getPropertyTypeFilter = type => {
  const routeKey = `rent_${type}`;
  return {
    [routeKey]: `for-rent:${type}`
  };
};

const getBudget = homecards => {
  const prices = homecards.map(homecard => homecard.displayPrice);
  const maxPrice = Math.max(...prices) * 1.1; // Adding 10% to the max price for a buffer
  return `0-${parseInt(maxPrice, 10)}`;
};

const AlternativesModal = ({
  listingId,
  moveIn = '',
  moveOut = '',
  cityId,
  isOpen,
  isClosable = true,
  handleClose = () => {}
}) => {
  const currentYear = getDateAsUTC(new Date()).getFullYear();
  const { params } = useSoyuzRequest();
  const { current: currentLocale } = useSoyuzLocales();
  const ecommerceManager = useEcommerceManagerFactory();
  const isUserLogged = useIsUserLogged();
  const { homecards, isLoading } = useAlternatives({
    listingId,
    moveIn,
    moveOut
  });

  const handleHomecardClick = (homecard, position) => () => {
    try {
      ecommerceManager.trackSimilarPropertyClick(
        homecard,
        isUserLogged,
        currentLocale,
        position
      );
    } catch (err) {
      console.error('Failed to handleHomecardClick at alternativesListings', {
        err
      });
    }

    SoyuzAnalytics.sendGA4Event('homecard-similar-properties', {
      action: 'click',
      section: 'listing-page'
    });
  };

  const handleOnSeeMoreProperties = e => {
    if (e) {
      e.preventDefault();
    }
    let destinationUrl;
    if (cityId) {
      const propertyTypeFilter = getPropertyTypeFilter(params.property);
      destinationUrl = getPagePath('marketplace.free-search', {
        params: {
          locationString: cityId,
          ...propertyTypeFilter
        },
        query: {
          'move-in': moveIn,
          'move-out': moveOut,
          budget: getBudget(homecards.slice(0, 3))
        }
      });
    } else {
      destinationUrl = getPagePath('landing');
    }

    SoyuzAnalytics.sendGA4Event('more-similar-properties', {
      action: 'click',
      section: 'button-show-more-properties'
    });

    window.location.href = destinationUrl;
  };

  return (
    <Modal
      isOpen={isOpen}
      size="big"
      contentLabel="modal"
      ariaHideApp={false}
      onRequestClose={handleClose}
      className="alternatives-modal"
    >
      <ModalHeader
        label={trans('listing.alternatives_modal.title')}
        onRequestClose={handleClose}
        className="alternatives-modal__header"
        showClose={isClosable}
      />
      <ModalBody className="alternatives-modal__homecards">
        {isLoading ? (
          <>
            <HomeCardPlaceHolder />
            <HomeCardPlaceHolder />
            <HomeCardPlaceHolder />
          </>
        ) : (
          <div className="alternatives-modal__homecards-list">
            {homecards.slice(0, 3).map((homecard, position) => {
              const firstAvailableDate = getDateAsUTC(
                new Date(homecard.firstAvailableDate)
              );
              const firstAvailableYear = firstAvailableDate?.getFullYear();
              const data = {
                ...homecard,
                photos: HomecardFormatter.getPhotos(
                  homecard.photoIds,
                  homecard.otherPropertyPhotoIds
                ),
                photoUrls: HomecardFormatter.getPhotoUrls(homecard.photoIds),
                monthlyPrice: getMonthlyPrice(homecard.displayPrice)
              };
              return (
                <div key={homecard.id}>
                  <Homecard
                    isShowStatus
                    data={data}
                    position={position}
                    currency={getCurrencySymbol(homecard)}
                    moveIn={moveIn}
                    moveOut={moveOut}
                    onClick={handleHomecardClick(homecard, position)}
                    hasLazyLoad
                    showAvailableFromYear={firstAvailableYear !== currentYear}
                    withTransparentBackground
                    pageType="product_page"
                    section="similar_properties"
                  />
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter className="alternatives-modal__footer">
        <Button
          color="tertiary"
          size="large"
          className="alternatives-modal__button"
          onClick={handleOnSeeMoreProperties}
        >
          {trans('alternatives_modal.button')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AlternativesModal.propTypes = {
  listingId: PropTypes.string.isRequired,
  moveIn: PropTypes.string,
  moveOut: PropTypes.string,
  cityId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isClosable: PropTypes.bool,
  handleClose: PropTypes.func
};

export default AlternativesModal;
